import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';

import { patient_inflow } from '../BaseURL';

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const MonthlyStatementOfOutpatients = ({ period }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartView, setChartView] = useState('bar');

  useEffect(() => {
    const fetchData = async () => {
      if (!period) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${patient_inflow}?period=${period}`);
        const result = await response.json();

        if (result.success) {
          processData(result.data);
        } else {
          setError('Failed to load data');
        }
      } catch (err) {
        setError('Error fetching data: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [period]);

const processData = (rawData) => {

  const ageGroups = [
    '0-28 Day',
    '01-11 Mon.',
    '01-4 Yrs',
    '05-09 Yrs',
    '10-14 Yrs',
    '15-17 Yrs',
    '18-19 Yrs',
    '20-34 Yrs',
    '35-49 Yrs',
    '50-59 Yrs',
    '60-69 Yrs',
    '70 + Yrs'
  ];

  const processedData = ageGroups.map(label => ({
    ageGroup: label,
    insured:    { new: { male: 0, female: 0 }, old: { male: 0, female: 0 } },
    nonInsured: { new: { male: 0, female: 0 }, old: { male: 0, female: 0 } },
    total: { male: 0, female: 0 }
  }));

  const ageMap = {};
  ageGroups.forEach((label, index) => { ageMap[label] = index; });

  rawData.forEach(patient => {
    const rowIndex = ageMap[patient.age];
    if (rowIndex === undefined) return;

    const row = processedData[rowIndex];

    const gender = patient.gender?.toLowerCase();
    const status = patient.status;
    const state  = patient.state;

    const isMale    = gender === 'male';
    const isNew     = status === 'NEW';

    // ✅ Any insurance that is NOT "Non Insured" counts as insured
    const isInsured = String(state || '').trim() !== 'Non Insured';

    if (isInsured) {
      if (isNew) {
        isMale ? row.insured.new.male++ : row.insured.new.female++;
      } else {
        isMale ? row.insured.old.male++ : row.insured.old.female++;
      }
    } else {
      if (isNew) {
        isMale ? row.nonInsured.new.male++ : row.nonInsured.new.female++;
      } else {
        isMale ? row.nonInsured.old.male++ : row.nonInsured.old.female++;
      }
    }

    isMale ? row.total.male++ : row.total.female++;
  });

  setData(processedData);
};


  const calculateGrandTotals = () => {
    const totals = {
      insured: {
        new: { male: 0, female: 0 },
        old: { male: 0, female: 0 }
      },
      nonInsured: {
        new: { male: 0, female: 0 },
        old: { male: 0, female: 0 }
      },
      total: { male: 0, female: 0 }
    };

    data.forEach(row => {
      totals.insured.new.male += row.insured.new.male;
      totals.insured.new.female += row.insured.new.female;
      totals.insured.old.male += row.insured.old.male;
      totals.insured.old.female += row.insured.old.female;
      totals.nonInsured.new.male += row.nonInsured.new.male;
      totals.nonInsured.new.female += row.nonInsured.new.female;
      totals.nonInsured.old.male += row.nonInsured.old.male;
      totals.nonInsured.old.female += row.nonInsured.old.female;
      totals.total.male += row.total.male;
      totals.total.female += row.total.female;
    });

    return totals;
  };

  const prepareChartData = () => {
    return data.map(row => ({
      name: row.ageGroup,
      'Insured Male': row.insured.new.male + row.insured.old.male,
      'Insured Female': row.insured.new.female + row.insured.old.female,
      'Non-Insured Male': row.nonInsured.new.male + row.nonInsured.old.male,
      'Non-Insured Female': row.nonInsured.new.female + row.nonInsured.old.female,
      'Total': row.total.male + row.total.female
    }));
  };

  const preparePieData = () => {
    const totals = calculateGrandTotals();
    return [
      { name: 'Insured Male', value: totals.insured.new.male + totals.insured.old.male },
      { name: 'Insured Female', value: totals.insured.new.female + totals.insured.old.female },
      { name: 'Non-Insured Male', value: totals.nonInsured.new.male + totals.nonInsured.old.male },
      { name: 'Non-Insured Female', value: totals.nonInsured.new.female + totals.nonInsured.old.female }
    ].filter(item => item.value > 0);
  };

  const prepareSummaryData = () => {
    const totals = calculateGrandTotals();
    const totalPatients = totals.total.male + totals.total.female;
    const totalInsured = totals.insured.new.male + totals.insured.new.female + 
                         totals.insured.old.male + totals.insured.old.female;
    const totalNonInsured = totals.nonInsured.new.male + totals.nonInsured.new.female + 
                            totals.nonInsured.old.male + totals.nonInsured.old.female;
    const totalNew = totals.insured.new.male + totals.insured.new.female + 
                     totals.nonInsured.new.male + totals.nonInsured.new.female;
    const totalOld = totals.insured.old.male + totals.insured.old.female + 
                     totals.nonInsured.old.male + totals.nonInsured.old.female;

    return [
      { title: 'Total Patients', value: totalPatients, color: '#11998e', icon: '👥' },
      { title: 'Insured', value: totalInsured, color: '#3b82f6', icon: '🛡️' },
      { title: 'Non-Insured', value: totalNonInsured, color: '#f59e0b', icon: '📋' },
      { title: 'New Cases', value: totalNew, color: '#10b981', icon: '🆕' },
      { title: 'Old Cases', value: totalOld, color: '#8b5cf6', icon: '📁' },
      { title: 'Male', value: totals.total.male, color: '#06b6d4', icon: '♂️' },
      { title: 'Female', value: totals.total.female, color: '#ec4899', icon: '♀️' }
    ];
  };

 

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!period) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Please select a period to view the monthly statement
        </Typography>
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Alert severity="info">No data available for the selected period</Alert>
      </Box>
    );
  }

  const grandTotals = calculateGrandTotals();
  const chartData = prepareChartData();
  const pieData = preparePieData();
  const summaryData = prepareSummaryData();
  const COLORS = ['#3b82f6', '#ec4899', '#10b981', '#f59e0b'];

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {summaryData.map((item, index) => (
          <Grid item xs={12} sm={6} md={3} lg={12/7} key={index}>
            <Card sx={{ 
              background: `linear-gradient(135deg, ${item.color} 0%, ${item.color}dd 100%)`,
              color: 'white',
              height: '100%'
            }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.75rem' }}>
                      {item.title}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                      {item.value}
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ opacity: 0.7 }}>
                    {item.icon}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Chart Controls */}
<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
  <Typography variant="h6" sx={{ fontWeight: 600 }}>
    Patient Distribution Chart
  </Typography>

  <ToggleButtonGroup
  value={chartView}
  exclusive
  onChange={(e, val) => val && setChartView(val)}
  size="small"
>
  <ToggleButton value="bar">Bar</ToggleButton>
  <ToggleButton value="line">Line</ToggleButton>
  <ToggleButton value="pie">Pie</ToggleButton>
</ToggleButtonGroup>
</Box>

<Paper sx={{ p: 2, mb: 3 }}>
  <ResponsiveContainer width="100%" height={350}>
    {chartView === 'bar' && (
      <BarChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis />
        <Tooltip />
        <Legend />
        <Bar dataKey="Insured Male" stackId="a" fill="#3b82f6" />
        <Bar dataKey="Insured Female" stackId="a" fill="#ec4899" />
        <Bar dataKey="Non-Insured Male" stackId="b" fill="#10b981" />
        <Bar dataKey="Non-Insured Female" stackId="b" fill="#f59e0b" />
      </BarChart>
    )}

    {chartView === 'line' && (
      <LineChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis />
        <Tooltip />
        <Legend />
        <Line type="monotone" dataKey="Insured Male" stroke="#3b82f6" />
        <Line type="monotone" dataKey="Insured Female" stroke="#ec4899" />
        <Line type="monotone" dataKey="Non-Insured Male" stroke="#10b981" />
        <Line type="monotone" dataKey="Non-Insured Female" stroke="#f59e0b" />
      </LineChart>
    )}

    {chartView === 'pie' && (
      <PieChart>
        <Pie
          data={pieData}
          dataKey="value"
          nameKey="name"
          outerRadius={120}
          label
        >
          {pieData.map((entry, index) => (
            <Cell key={index} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip />
        <Legend />
      </PieChart>
    )}
  </ResponsiveContainer>
</Paper>

      {/* Data Table */}
      <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
        <Table size="small" sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#4a5568' }}>
              <TableCell 
                rowSpan={3} 
                sx={{ 
                  color: 'white', 
                  fontWeight: 'bold', 
                  borderRight: '1px solid rgba(255,255,255,0.3)',
                  textAlign: 'center',
                  verticalAlign: 'middle'
                }}
              >
                AGE GROUPS
              </TableCell>
              <TableCell 
                colSpan={4} 
                align="center" 
                sx={{ 
                  color: 'white', 
                  fontWeight: 'bold',
                  borderRight: '1px solid rgba(255,255,255,0.3)'
                }}
              >
                INSURED PATIENTS
              </TableCell>
              <TableCell 
                colSpan={4} 
                align="center" 
                sx={{ 
                  color: 'white', 
                  fontWeight: 'bold',
                  borderRight: '1px solid rgba(255,255,255,0.3)'
                }}
              >
                NON-INSURED PATIENTS
              </TableCell>
              <TableCell 
                colSpan={2} 
                align="center" 
                sx={{ 
                  color: 'white', 
                  fontWeight: 'bold'
                }}
              >
                TOTAL
              </TableCell>
            </TableRow>

            <TableRow sx={{ bgcolor: '#4a5568' }}>
              <TableCell 
                colSpan={2} 
                align="center" 
                sx={{ 
                  color: 'white', 
                  fontWeight: 'bold',
                  borderRight: '1px solid rgba(255,255,255,0.3)',
                  fontSize: '0.85rem'
                }}
              >
                NEW
              </TableCell>
              <TableCell 
                colSpan={2} 
                align="center" 
                sx={{ 
                  color: 'white', 
                  fontWeight: 'bold',
                  borderRight: '1px solid rgba(255,255,255,0.3)',
                  fontSize: '0.85rem'
                }}
              >
                OLD
              </TableCell>
              <TableCell 
                colSpan={2} 
                align="center" 
                sx={{ 
                  color: 'white', 
                  fontWeight: 'bold',
                  borderRight: '1px solid rgba(255,255,255,0.3)',
                  fontSize: '0.85rem'
                }}
              >
                NEW
              </TableCell>
              <TableCell 
                colSpan={2} 
                align="center" 
                sx={{ 
                  color: 'white', 
                  fontWeight: 'bold',
                  borderRight: '1px solid rgba(255,255,255,0.3)',
                  fontSize: '0.85rem'
                }}
              >
                OLD
              </TableCell>
              <TableCell 
                rowSpan={2}
                align="center" 
                sx={{ 
                  color: 'white', 
                  fontWeight: 'bold',
                  fontSize: '0.85rem',
                  verticalAlign: 'middle'
                }}
              >
                MALE
              </TableCell>
              <TableCell 
                rowSpan={2}
                align="center" 
                sx={{ 
                  color: 'white', 
                  fontWeight: 'bold',
                  fontSize: '0.85rem',
                  verticalAlign: 'middle'
                }}
              >
                FEMALE
              </TableCell>
            </TableRow>

            <TableRow sx={{ bgcolor: '#4a5568' }}>
              {[...Array(8)].map((_, index) => (
                <TableCell 
                  key={index}
                  align="center" 
                  sx={{ 
                    color: 'white', 
                    fontSize: '0.75rem',
                    borderRight: index === 1 || index === 3 || index === 5 || index === 7 
                      ? '1px solid rgba(255,255,255,0.3)' 
                      : 'none'
                  }}
                >
                  {index % 2 === 0 ? 'MALE' : 'FEMALE'}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {data.map((row, index) => (
              <TableRow 
                key={index}
                sx={{ 
                  '&:nth-of-type(odd)': { bgcolor: '#f7fafc' },
                  '&:hover': { bgcolor: '#edf2f7' }
                }}
              >
                <TableCell sx={{ fontWeight: 500, borderRight: '1px solid #e2e8f0' }}>
                  {row.ageGroup}
                </TableCell>
                
                <TableCell align="center">{row.insured.new.male || 0}</TableCell>
                <TableCell align="center" sx={{ borderRight: '1px solid #e2e8f0' }}>
                  {row.insured.new.female || 0}
                </TableCell>
                
                <TableCell align="center">{row.insured.old.male || 0}</TableCell>
                <TableCell align="center" sx={{ borderRight: '1px solid #e2e8f0' }}>
                  {row.insured.old.female || 0}
                </TableCell>
                
                <TableCell align="center">{row.nonInsured.new.male || 0}</TableCell>
                <TableCell align="center" sx={{ borderRight: '1px solid #e2e8f0' }}>
                  {row.nonInsured.new.female || 0}
                </TableCell>
                
                <TableCell align="center">{row.nonInsured.old.male || 0}</TableCell>
                <TableCell align="center" sx={{ borderRight: '1px solid #e2e8f0' }}>
                  {row.nonInsured.old.female || 0}
                </TableCell>
                
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  {row.total.male || 0}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  {row.total.female || 0}
                </TableCell>
              </TableRow>
            ))}

            <TableRow sx={{ bgcolor: '#e2e8f0', fontWeight: 'bold' }}>
              <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #cbd5e0' }}>
                Total All Ages
              </TableCell>
              
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                {grandTotals.insured.new.male}
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #cbd5e0' }}>
                {grandTotals.insured.new.female}
              </TableCell>
              
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                {grandTotals.insured.old.male}
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #cbd5e0' }}>
                {grandTotals.insured.old.female}
              </TableCell>
              
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                {grandTotals.nonInsured.new.male}
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #cbd5e0' }}>
                {grandTotals.nonInsured.new.female}
              </TableCell>
              
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                {grandTotals.nonInsured.old.male}
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', borderRight: '1px solid #cbd5e0' }}>
                {grandTotals.nonInsured.old.female}
              </TableCell>
              
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                {grandTotals.total.male}
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                {grandTotals.total.female}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default MonthlyStatementOfOutpatients;